import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ListChecks } from 'lucide-react';
import { type SprintView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { SprintColorPicker } from '../../sprints/components/sprint-color-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
interface BacklogActionsProps {
  readonly count: number;
  /** Sprint còn nhận việc được: đang chạy hoặc đang lập kế hoạch. */
  readonly sprints: readonly SprintView[];
  readonly pending: boolean;
  readonly onMove: (sprintId: string) => void;
  readonly onCreate: (name: string, color: string | null) => void;
  readonly onClear: () => void;
}

/**
 * Thanh hành động của cột Backlog — hiện khi đã chọn ít nhất một việc.
 *
 * ## Vì sao chọn-nhiều, không phải kéo từng thẻ
 *
 * Lập sprint là thao tác **theo lô**: người ta nhìn cả hàng đợi rồi quyết định
 * "mười việc này vào sprint tới". Kéo từng thẻ một cho mười việc là mười cú
 * kéo và mười lần chờ, và giữa chừng thì hàng đợi đã xô lệch vị trí.
 *
 * Kéo vẫn còn cho trường hợp một việc — hai cách cho hai tình huống khác nhau.
 */
export function BacklogActions({
  count,
  sprints,
  pending,
  onMove,
  onCreate,
  onClear,
}: BacklogActionsProps): ReactNode {
  const { t } = useTranslation();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);

  if (count === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-DEFAULT border border-primary/40 bg-primary/5 p-2">
      <ListChecks className="size-4 text-primary" aria-hidden />
      <span className="text-sm font-medium">{t('board.selectedCount', { count })}</span>

      {naming ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length === 0) return;
            onCreate(trimmed, color);
            setNaming(false);
            setName('');
            setColor(null);
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('board.newSprintName')}
            aria-label={t('board.newSprintName')}
            className="h-8 rounded-control border border-border-strong bg-background px-2 text-sm"
          />
          {/* Chọn màu ngay lúc tạo: sprint mới sinh ra là để đặt cạnh sprint
              đang chạy, và đó đúng là lúc cần phân biệt hai cái. */}
          <SprintColorPicker value={color} onChange={setColor} />

          <Button type="submit" size="sm" disabled={pending || name.trim().length === 0}>
            {t('board.createAndMove')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setNaming(false)}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={pending}>
                <CalendarPlus aria-hidden />
                {t('board.moveSelectedToSprint')}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('board.chooseSprint')}
              </DropdownMenuLabel>

              {sprints.map((sprint) => (
                <DropdownMenuItem key={sprint.id} onSelect={() => onMove(sprint.id)}>
                  {sprint.name}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {t(`board.sprintStatus.${sprint.status}`)}
                  </span>
                </DropdownMenuItem>
              ))}

              {sprints.length === 0 ? (
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('board.noOpenSprint')}
                </DropdownMenuLabel>
              ) : null}

              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setNaming(true)}>
                <CalendarPlus aria-hidden className="size-4" />
                {t('board.newSprint')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" variant="ghost" onClick={onClear}>
            {t('board.clearSelection')}
          </Button>
        </>
      )}
    </div>
  );
}
